import { useState } from "react";
import { Modal } from "antd";
import { useMutation } from "@apollo/client";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { scheduleBulkDeleteDutiesMutation } from "@src/util/mutation/schedule.mutation";
import { stopRotaRollingMutation } from "@src/util/mutation/rotaLoadBatch.mutation";
import { stopMobileServiceTemplatesBySiteIdMutation } from "@src/util/mutation/mobileServices.mutation";
import { requestReturnKeyMutation, returnKeyToCustomerMutation } from "@src/util/mutation/keyRegister.mutation";
import { KeyActionModal } from "@src/pages/keyRegister/keyActionModal";

export const TERMINATED_STATUS = "Terminated";
export const TERMINATION_PENDING_STATUS = "Termination Pending";

const SCHEDULE_STATUS_LABEL = { "0": "Open", "1": "Confirmed", "2": "Unconfirmed", "3": "Completed" };

// A key already sitting "In House" (physically back at the office, custodianId cleared - see
// allKeys.service.ts's returnKey) has no external party left to request it back from, so it skips
// the request step entirely and goes straight to "Return to Customer". Matched loosely since this
// value isn't a fixed enum ("In House", "inhouse-staff", ...).
const isInHouseCustody = (custodianType) => /in.?house/i.test(String(custodianType || ""));

const custodianLabel = (custodianType) => {
    if (custodianType === "supplier") return "Supplier";
    if (custodianType === "employee") return "Employee";
    if (isInHouseCustody(custodianType)) return "In House";
    return custodianType || "someone";
};

// Green "all clear" line vs. the section's own content - same "quiet unless
// there's something to act on" language used elsewhere in the site detail
// page (e.g. the duplicate-site / address-change overlays).
const Section = ({ title, isClear, clearLabel, children }) => (
    <div style={{ border: "1px solid #f0f0f0", borderRadius: 6, padding: "12px 14px", marginBottom: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: isClear ? 4 : 8, fontSize: 13 }}>{title}</div>
        {isClear ? (
            <div style={{ color: "#389e0d", fontSize: 13 }}>✓ {clearLabel}</div>
        ) : children}
    </div>
);

const Banner = ({ tone, children }) => (
    <div style={{
        background: tone === "red" ? "#fff1f0" : "#fffbe6",
        border: `1px solid ${tone === "red" ? "#ffa39e" : "#ffe58f"}`,
        borderRadius: 4,
        padding: "8px 10px",
        fontSize: 12.5,
        color: tone === "red" ? "#a8071a" : "#874d00",
        marginBottom: 8,
    }}>
        {children}
    </div>
);

/**
 * Pre-save gate for the Site detail page's "Site Status → Terminated" flow.
 * Shows the four things that can be silently orphaned by a termination -
 * an auto-rolling rota, guarding duties beyond the contract end date,
 * active mobile service templates/loaded duties, and outstanding keys -
 * each with its own resolve action, wired to getSiteTerminationImpact's
 * response shape. Rota/schedules/mobile are hard gates - "Proceed" stays
 * disabled until each is fully clear. Keys held by a supplier/employee are
 * softer: once every such key's return has been *requested* (not necessarily
 * received yet), Proceed unlocks but calls onProceed with "Termination
 * Pending" instead of "Terminated" - the caller is responsible for actually
 * writing that status (see siteDetail.page.jsx / editTaskMainModal.jsx). A
 * key already sitting In House has no request step and no travel time, so it
 * always hard-blocks until it's actually handed to the customer.
 */
export const SiteTerminationCheckModal = ({ open, siteId, contractEndDate, impact, checking, onRefresh, onCancel, onProceed }) => {
    const dispatch = useDispatch();
    const [busySection, setBusySection] = useState(null); // 'rota' | 'schedules' | 'mobile' | null
    const [keyAction, setKeyAction] = useState(null); // {type: 'Request Return'|'Return to Customer', key: <row>} | null

    const [stopRotaRolling] = useMutation(stopRotaRollingMutation);
    const [deleteOpenSchedules] = useMutation(scheduleBulkDeleteDutiesMutation);
    const [stopMobileTemplates] = useMutation(stopMobileServiceTemplatesBySiteIdMutation);
    const [requestReturnKey, { loading: requestReturnKeyLoading }] = useMutation(requestReturnKeyMutation);
    const [returnKeyToCustomer, { loading: returnKeyToCustomerLoading }] = useMutation(returnKeyToCustomerMutation);

    const response = impact?.response ?? {};
    const rotaIsRolling = !!response?.rotaIsRolling;
    const openSchedules = response?.openSchedules ?? [];
    const blockingSchedules = response?.blockingSchedules ?? [];
    const mobileTemplates = response?.mobileTemplates ?? [];
    const outstandingKeys = response?.outstandingKeys ?? [];

    // A supplier/employee-held key only hard-blocks while its return hasn't even been requested
    // yet - once requested, termination can proceed and lands the site in Termination Pending
    // until it actually comes back. An In House key has no travel time and no external party to
    // wait on, so it always hard-blocks until it's actually handed to the customer (at which point
    // it drops out of outstandingKeys entirely, same as any other resolved key).
    const keysBlockProceed = outstandingKeys.some((k) => isInHouseCustody(k.custodianType) || !k.returnRequested);

    const canProceed = !checking && !rotaIsRolling
        && openSchedules.length === 0 && blockingSchedules.length === 0
        && mobileTemplates.length === 0 && !keysBlockProceed;

    const finalStatus = outstandingKeys.length === 0 ? TERMINATED_STATUS : TERMINATION_PENDING_STATUS;

    const runAction = async (section, fn) => {
        setBusySection(section);
        try {
            await fn();
            await onRefresh();
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err?.message }));
        } finally {
            setBusySection(null);
        }
    };

    const handleStopRolling = () => runAction("rota", () => stopRotaRolling({ variables: { input: { siteIds: [siteId] } } }));

    const handleCancelOpenSchedules = () => runAction("schedules", () => deleteOpenSchedules({
        variables: { scheduleIds: openSchedules.map((s) => s._id), reason: "Site terminated" },
    }));

    const handleStopMobileTemplates = () => runAction("mobile", () => stopMobileTemplates({
        variables: { siteId, stopDate: contractEndDate, reason: "Site terminated" },
    }));

    const handleKeyActionSubmit = async (type, form, customerId) => {
        try {
            const values = form.getFieldsValue();
            if (type === "Request Return") {
                await requestReturnKey({ variables: { input: { ...values, _id: keyAction?.key?._id } } });
            } else if (type === "Return to Customer") {
                await returnKeyToCustomer({ variables: { input: { ...values, _id: keyAction?.key?._id, customerId } } });
            }
            setKeyAction(null);
            await onRefresh();
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err?.message }));
        }
    };

    return (
        <Modal
            open={open}
            width="min(560px, 92vw)"
            style={{ top: 10 }}
            closable={false}
            footer={
                <div className="drawer-footer" style={{ justifyContent: "flex-end" }}>
                    <button className="drawer-outlined-btn" onClick={onCancel}>Cancel</button>
                    <button
                        className={canProceed ? "drawer-filled-btn" : "disabled-btn drawer-filled-btn"}
                        disabled={!canProceed}
                        onClick={() => onProceed(finalStatus)}
                    >
                        {checking ? <Spinner /> : outstandingKeys.length > 0 ? "Proceed (Termination Pending)" : "Proceed with Termination"}
                    </button>
                </div>
            }
        >
            <div className="modal-header-title">
                <span>Terminating this site</span>
                <span onClick={onCancel}><FontAwesomeIcon className="close" icon={faClose}/></span>
            </div>

            <div className="modal-body" style={{ color: "#1e2d3d", fontWeight: 400 }}>
                <p style={{ fontSize: 13, color: "#595959", marginBottom: 14, marginTop: 0 }}>
                    Before this site is set to Terminated, here's what's still live beyond the contract end date.
                </p>

                <Section title="Auto-rolling rota" isClear={!rotaIsRolling} clearLabel="Not set to auto-roll.">
                    <Banner tone="amber">This site's rota is set to auto-roll and will keep generating new guarding duties until stopped.</Banner>
                    <button className="drawer-filled-btn" disabled={busySection === "rota"} onClick={handleStopRolling}>
                        {busySection === "rota" ? "Stopping…" : "Stop Rolling"}
                    </button>
                </Section>

                <Section
                    title="Guarding duties"
                    isClear={openSchedules.length === 0 && blockingSchedules.length === 0}
                    clearLabel="No guarding duties beyond the contract end date."
                >
                    {blockingSchedules.length > 0 && (
                        <>
                            <Banner tone="red">
                                {blockingSchedules.length} dut{blockingSchedules.length > 1 ? "ies are" : "y is"} still {" "}
                                {[...new Set(blockingSchedules.map((s) => SCHEDULE_STATUS_LABEL[s.status] || s.status))].join("/")} beyond the contract end date.
                                Please reassign or cancel {blockingSchedules.length > 1 ? "them" : "it"} from the Schedule page before terminating.
                            </Banner>
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
                                {blockingSchedules.map((s) => (
                                    <li key={s._id}>
                                        {dayjs.utc(s.firstDate).format("DD/MM/YYYY HH:mm")} — {SCHEDULE_STATUS_LABEL[s.status] || s.status}
                                        {s.assignedEmployeeName ? ` — ${s.assignedEmployeeName}` : ""}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {openSchedules.length > 0 && (
                        <>
                            <ul style={{ margin: "0 0 8px", paddingLeft: 18, fontSize: 12.5 }}>
                                {openSchedules.map((s) => (
                                    <li key={s._id}>{dayjs.utc(s.firstDate).format("DD/MM/YYYY HH:mm")} — Open</li>
                                ))}
                            </ul>
                            <button className="drawer-filled-btn" disabled={busySection === "schedules"} onClick={handleCancelOpenSchedules}>
                                {busySection === "schedules" ? "Cancelling…" : `Cancel ${openSchedules.length} open dut${openSchedules.length > 1 ? "ies" : "y"}`}
                            </button>
                        </>
                    )}
                </Section>

                <Section title="Mobile services" isClear={mobileTemplates.length === 0} clearLabel="No active mobile service templates for this site.">
                    <ul style={{ margin: "0 0 8px", paddingLeft: 18, fontSize: 12.5 }}>
                        {mobileTemplates.map((t) => (
                            <li key={t._id}>{t.scheduleType} — {t.loadedDutiesBeyondCount} loaded dut{t.loadedDutiesBeyondCount === 1 ? "y" : "ies"} beyond the contract end date</li>
                        ))}
                    </ul>
                    <button className="drawer-filled-btn" disabled={busySection === "mobile"} onClick={handleStopMobileTemplates}>
                        {busySection === "mobile" ? "Stopping…" : `Stop template${mobileTemplates.length > 1 ? "s" : ""} & cancel loaded duties`}
                    </button>
                </Section>

                <Section title="Site key" isClear={outstandingKeys.length === 0} clearLabel="No outstanding keys for this site.">
                    <Banner tone={keysBlockProceed ? "red" : "amber"}>
                        {keysBlockProceed
                            ? "Every key still out needs to be requested back (or, if already In House, returned to the customer) before terminating."
                            : "Return has been requested for every outstanding key - the site will show Termination Pending until they're actually back with the customer."}
                    </Banner>
                    <ul style={{ margin: 0, padding: 0, listStyle: "none", fontSize: 12.5 }}>
                        {outstandingKeys.map((k) => {
                            const inHouse = isInHouseCustody(k.custodianType);
                            return (
                                <li key={k._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: "1px solid #f0f0f0" }}>
                                    <span>
                                        {k.keySerial} — in custody of {custodianLabel(k.custodianType)}
                                        {k.custodianName ? `: ${k.custodianName}` : ""}
                                    </span>
                                    {inHouse ? (
                                        <button className="drawer-outlined-btn" style={{ padding: "4px 10px" }} onClick={() => setKeyAction({ type: "Return to Customer", key: k })}>
                                            Return to Customer
                                        </button>
                                    ) : k.returnRequested ? (
                                        <span style={{ color: "#874d00" }}>
                                            Requested{k.returnRequestExpectedDate ? ` — expected ${dayjs.utc(k.returnRequestExpectedDate).format("DD/MM/YYYY")}` : ""}
                                        </span>
                                    ) : (
                                        <button className="drawer-outlined-btn" style={{ padding: "4px 10px" }} onClick={() => setKeyAction({ type: "Request Return", key: k })}>
                                            Send Return Request
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </Section>
            </div>

            {keyAction && (
                <KeyActionModal
                    visible={!!keyAction}
                    type={keyAction.type}
                    record={keyAction.key}
                    close={() => setKeyAction(null)}
                    loading={requestReturnKeyLoading || returnKeyToCustomerLoading}
                    handleActionSubmit={handleKeyActionSubmit}
                />
            )}
        </Modal>
    );
};
